import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Printer, Download, Check, X } from 'lucide-react';
import api from '../services/api';

const StandardsReportPage = () => {
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('child');
  const [child, setChild] = useState(null);
  const [schoolName, setSchoolName] = useState('Homeschool');
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    loadInit();
  }, [childId]);

  useEffect(() => {
    if (selectedSemester) loadReport();
  }, [selectedSemester]);

  const loadInit = async () => {
    try {
      const [childrenData, userData] = await Promise.all([
        api.getChildren(),
        api.getCurrentUser(),
      ]);
      const childData = childrenData.find(c => c.id === childId);
      setChild(childData);
      setSchoolName(userData.school_name || 'Homeschool');

      if (userData.semesters && userData.semesters.length > 0) {
        const normalized = userData.semesters.map(sem => ({
          name: sem.name,
          start_date: sem.start_date || sem.startDate,
          end_date: sem.end_date || sem.endDate,
        }));
        setSemesters(normalized);
        setSelectedSemester(normalized[0]);
      } else {
        const defaults = [
          { name: 'Fall 2024', start_date: '2024-08-01', end_date: '2024-12-31' },
          { name: 'Spring 2025', start_date: '2025-01-01', end_date: '2025-05-31' },
        ];
        setSemesters(defaults);
        setSelectedSemester(defaults[0]);
      }
    } catch (error) {
      console.error('Failed to load report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    setLoadingReport(true);
    try {
      const data = await api.getStandardsCoverage(
        childId,
        selectedSemester.start_date,
        selectedSemester.end_date
      );
      setReport(data);
    } catch (error) {
      console.error('Failed to load standards coverage:', error);
      setReport(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return <div className="p-8 text-center">Loading standards report...</div>;
  }
  if (!child) {
    return <div className="p-8 text-center">Student not found</div>;
  }
  if (!selectedSemester) {
    return <div className="p-8 text-center">No semester data available. Please configure semesters in Settings.</div>;
  }

  const totals = report?.totals || { total: 0, covered: 0, not_covered: 0 };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Controls - hidden when printing */}
      <div className="no-print sticky top-0 z-10 bg-white border-b p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Semester:</label>
          <select
            value={selectedSemester.name}
            onChange={(e) => {
              const semester = semesters.find(s => s.name === e.target.value);
              setSelectedSemester(semester);
            }}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="standards-report-semester-select"
          >
            {semesters.map((sem, idx) => (
              <option key={idx} value={sem.name}>{sem.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-3">
          <Button onClick={handlePrint} variant="outline" data-testid="standards-report-print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print Report
          </Button>
          <Button onClick={handlePrint} className="bg-blue-600" data-testid="standards-report-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            Save as PDF
          </Button>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-4xl mx-auto bg-white print:mx-0 print:max-w-none print:pt-0" style={{ padding: '1in', paddingTop: '1in' }}>
        {/* Header */}
        <div className="text-center mb-8 border-b-4 border-blue-600 pb-6 print:border-b-2 print:border-slate-300">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Standards Report</h1>
          <p className="text-lg text-slate-600">{schoolName}</p>
        </div>

        {/* Student Information */}
        <div className="mb-8 p-6 bg-blue-50 rounded-lg print:bg-transparent print:border print:border-slate-300">
          <h2 className="text-xl font-bold text-slate-900 mb-4">Student Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-600">Student Name</p>
              <p className="text-lg font-semibold text-slate-900">{child.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Grade</p>
              <p className="text-lg font-semibold text-slate-900">{child.grade || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">Semester</p>
              <p className="text-lg font-semibold text-slate-900">{selectedSemester.name}</p>
            </div>
            <div>
              <p className="text-sm text-slate-600">State</p>
              <p className="text-lg font-semibold text-slate-900">{report?.state || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="mb-8 border border-slate-300 rounded-lg p-6 bg-slate-50 print:bg-transparent">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">{totals.covered}</p>
              <p className="text-sm text-slate-600">Covered</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-500">{totals.not_covered}</p>
              <p className="text-sm text-slate-600">Not Covered</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{totals.total}</p>
              <p className="text-sm text-slate-600">Total Standards</p>
            </div>
          </div>
        </div>

        {/* Standards by Subject */}
        {loadingReport ? (
          <div className="p-8 text-center text-slate-500">Loading standards…</div>
        ) : !report || report.subjects.length === 0 ? (
          <div className="border border-slate-300 rounded-lg p-6 text-center text-slate-600" data-testid="no-standards-report-msg">
            No standards found for {child.grade || 'this grade'} in {report?.state || 'your state'}.
            Add standards in the Admin panel, then tag them on lesson plans and assignments.
          </div>
        ) : (
          report.subjects.map((subj, idx) => (
            <div key={idx} className="mb-8" data-testid={`report-subject-${subj.subject}`}>
              <div className="flex items-center justify-between mb-3 border-b border-slate-300 pb-2">
                <h2 className="text-xl font-bold text-slate-900">{subj.subject}</h2>
                <span className="text-sm font-medium text-slate-600">
                  {subj.covered_count} / {subj.total} covered
                </span>
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="border border-slate-300 p-2 text-left w-40">Standard</th>
                    <th className="border border-slate-300 p-2 text-left">Description</th>
                    <th className="border border-slate-300 p-2 text-center w-28">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subj.standards.map((s) => (
                    <tr key={s.id} className="hover:bg-slate-50" data-testid={`report-standard-${s.id}`}>
                      <td className="border border-slate-300 p-2 font-mono text-sm font-semibold align-top">{s.code}</td>
                      <td className="border border-slate-300 p-2 text-sm text-slate-700">{s.description}</td>
                      <td className="border border-slate-300 p-2 text-center">
                        {s.covered ? (
                          <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                            <Check className="w-4 h-4" /> Covered
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-500 font-semibold">
                            <X className="w-4 h-4" /> Not Covered
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}

        {/* Footer */}
        <div className="mt-12 pt-6 border-t border-slate-300">
          <p className="text-center text-sm text-slate-600">
            This report reflects academic standards coverage for {child.name} during {selectedSemester.name}
          </p>
          <p className="text-center text-sm text-slate-500 mt-2">
            Generated on {new Date().toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx global>{`
        @media print {
          * {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .no-print { display: none !important; }
          body { background: white !important; margin: 0 !important; padding: 0 !important; }
          body::before, body::after { display: none !important; }
          header, nav, .header, [role="banner"] { display: none !important; }
          @page { margin: 1.25in 1in 1in 1in; size: letter; }
          .print\\:mx-0 { margin-left: 0 !important; margin-right: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:pt-0 { padding-top: 0 !important; }
          .print\\:bg-transparent { background: transparent !important; }
          .print\\:border { border: 1px solid #cbd5e1 !important; }
          .print\\:border-b-2 { border-bottom-width: 2px !important; }
          .print\\:border-slate-300 { border-color: #cbd5e1 !important; }
        }
      `}</style>
    </div>
  );
};

export default StandardsReportPage;
